package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMComboBoxListMaxHeightVar
import ai.wagemix.web.theme.WMControlCornerVar
import ai.wagemix.web.theme.WMControlHeightVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMInputPaddingInlineVar
import ai.wagemix.web.theme.WMOpacity
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.PointerEvents
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.pointerEvents
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.disabled
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import kotlin.random.Random

/** The text field of a [WMComboBox]: the select's frame, with room on the right for the chevron. */
val WMComboBoxInputStyle = CssStyle {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .height(WMControlHeightVar.value())
            .padding(left = WMInputPaddingInlineVar.value())
            .borderRadius(WMControlCornerVar.value())
            .border(WMHairlineVar.value(), LineStyle.Solid, colors.outline)
            .backgroundColor(colors.surface)
            .color(colors.onSurface)
            .styleModifier {
                property("padding-right", "calc(2 * ${WMInputPaddingInlineVar.value()} + ${WMIconSizeVar.value()})")
                property("box-sizing", "border-box")
                property("text-overflow", "ellipsis")
            }
    }
    focus {
        Modifier.outline(0.px).border(WMHairlineVar.value(), LineStyle.Solid, colorMode.toPalette().fs.primary)
    }
    disabled {
        Modifier.cursor(Cursor.NotAllowed).opacity(WMOpacity.DISABLED)
    }
}

/** The list under a [WMComboBox]: a raised surface as wide as the field, scrolling past about eight rows. */
val WMComboBoxListStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .position(Position.Absolute)
        .top(100.percent)
        .left(0.px)
        .right(0.px)
        .zIndex(COMBO_BOX_Z_INDEX)
        .maxHeight(WMComboBoxListMaxHeightVar.value())
        .overflow { y(Overflow.Auto) }
        .padding(WMTightGapVar.value())
        .borderRadius(WMControlCornerVar.value())
        .border(WMHairlineVar.value(), LineStyle.Solid, colors.outlineVariant)
        .backgroundColor(colors.surface)
        .styleModifier { property("margin-top", WMTightGapVar.value().toString()) }
}

/** The kind of [WMComboBoxOptionStyle], so it can have the active and selected variants. */
sealed interface WMComboBoxOptionKind: ComponentKind

/** One choice in the list. */
val WMComboBoxOptionStyle = CssStyle<WMComboBoxOptionKind> {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = WMTightGapVar.value(), leftRight = WMInlineGapVar.value())
            .borderRadius(WMControlCornerVar.value())
            .color(colorMode.toPalette().fs.onSurface)
            .cursor(Cursor.Pointer)
    }
    hover { Modifier.backgroundColor(colorMode.toPalette().fs.surfaceContainer) }
}

/** The choice the arrow keys are on; Enter picks it. */
val WMActiveComboBoxOptionVariant = WMComboBoxOptionStyle.addVariant {
    base { Modifier.backgroundColor(colorMode.toPalette().fs.surfaceContainer) }
}

/** The current value, in the primary colour. */
val WMSelectedComboBoxOptionVariant = WMComboBoxOptionStyle.addVariant {
    base { Modifier.color(colorMode.toPalette().fs.primary) }
}

/** The options whose label contains every word of [query], ignoring case; all of them for a blank query. */
fun List<WMSelectOption>.matching(query: String): List<WMSelectOption> {
    val words = query.lowercase().split(' ', ',').filter { it.isNotBlank() }
    if (words.isEmpty()) {
        return this
    }
    return filter { option ->
        val label = option.label.lowercase()
        words.all { it in label }
    }
}

/**
 * Penpot `Wagemix / Input` (Type: Select) for long lists: a text field that opens the whole list, and narrows it
 * to the options containing what the user types. Arrow keys move through the list, Enter picks, Escape closes;
 * leaving the field without picking keeps the current value. It follows the ARIA combobox pattern (a `combobox`
 * input over a `listbox`), so assistive technology announces it as a select that can be typed into.
 */
@Composable
fun WMComboBox(
    options: List<WMSelectOption>,
    selectedValue: String?,
    accessibilityString: String,
    noMatchesText: String,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    onSelect: (String) -> Unit
) {
    val colors = ColorMode.current.toPalette().fs
    val listId = remember { "wm-combo-${Random.nextInt(0, Int.MAX_VALUE).toString(36)}" }
    // `null` while the user is not typing: the field then shows the selected option's label.
    var query by remember { mutableStateOf<String?>(null) }
    var open by remember { mutableStateOf(false) }
    var active by remember { mutableStateOf(0) }
    val selectedLabel = options.firstOrNull { it.value == selectedValue }?.label.orEmpty()
    val shown = if (open) options.matching(query.orEmpty()) else emptyList()

    fun close() {
        open = false
        query = null
    }

    fun pick(option: WMSelectOption) {
        close()
        if (option.value != selectedValue) {
            onSelect(option.value)
        }
    }

    LaunchedEffect(open, active) {
        if (open) {
            document.getElementById("$listId-$active")?.asDynamic()?.scrollIntoView(js("({ block: 'nearest' })"))
        }
    }

    Box(modifier = Modifier.fillMaxWidth().position(Position.Relative).then(modifier)) {
        Input(
            type = InputType.Text,
            attrs = WMComboBoxInputStyle.toModifier().then(WMBodyTextStyle.toModifier()).toAttrs {
                value(query ?: selectedLabel)
                // While typing, the current value stays visible as the placeholder.
                attr("placeholder", selectedLabel)
                attr("role", "combobox")
                attr("aria-label", accessibilityString)
                attr("aria-autocomplete", "list")
                attr("aria-controls", listId)
                attr("aria-expanded", open.toString())
                attr("autocomplete", "off")
                if (open && shown.isNotEmpty()) {
                    attr("aria-activedescendant", "$listId-$active")
                }
                if (!enabled) {
                    attr("disabled", "")
                }
                onFocus {
                    query = ""
                    active = options.indexOfFirst { it.value == selectedValue }.coerceAtLeast(0)
                    open = true
                }
                onBlur { close() }
                onInput { event ->
                    query = event.value
                    active = 0
                    open = true
                }
                onKeyDown { event ->
                    when (event.key) {
                        "ArrowDown" -> {
                            event.preventDefault()
                            open = true
                            active = (active + 1).coerceAtMost((shown.size - 1).coerceAtLeast(0))
                        }
                        "ArrowUp" -> {
                            event.preventDefault()
                            active = (active - 1).coerceAtLeast(0)
                        }
                        "Enter" -> {
                            event.preventDefault()
                            shown.getOrNull(active)?.let { pick(it) }
                        }
                        "Escape" -> close()
                    }
                }
            }
        )
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .right(WMInputPaddingInlineVar.value())
                .top(50.percent)
                .translateY((-50).percent)
                .color(colors.onSurfaceVariant)
                .pointerEvents(PointerEvents.None)
        ) {
            WMIcon(WMIconGlyph.ChevronDown, accessibilityString = null, size = WMIconSizeVar.value())
        }
        if (open) {
            Column(
                modifier = WMComboBoxListStyle.toModifier()
                    .then(WMRaisedStyle.toModifier())
                    .attrsModifier {
                        id(listId)
                        attr("role", "listbox")
                        attr("aria-label", accessibilityString)
                    }
            ) {
                if (shown.isEmpty()) {
                    SpanText(
                        noMatchesText,
                        WMComboBoxOptionStyle.toModifier().color(colors.onSurfaceVariant).cursor(Cursor.Default)
                    )
                }
                shown.forEachIndexed { index, option ->
                    val variants = listOfNotNull(
                        WMActiveComboBoxOptionVariant.takeIf { index == active },
                        WMSelectedComboBoxOptionVariant.takeIf { option.value == selectedValue },
                    )
                    SpanText(
                        option.label,
                        WMComboBoxOptionStyle.toModifier(*variants.toTypedArray())
                            .then(WMBodyTextStyle.toModifier())
                            .attrsModifier {
                                id("$listId-$index")
                                attr("role", "option")
                                attr("aria-selected", (option.value == selectedValue).toString())
                                // Keep the field focused, so the blur does not close the list before the click.
                                onMouseDown { it.preventDefault() }
                                onClick { pick(option) }
                            }
                    )
                }
            }
        }
    }
}

private const val COMBO_BOX_Z_INDEX = 30
